import {MutableRefObject, useMemo, useRef, useState} from "react"
import {isMobile} from "react-device-detect"
import cx from "classnames"
import {Icon} from "components/Icon"
import styles from "./item.module.css"
import {RemarkIndicator} from "./RemarkIndicator"
import {PreviousAnswer} from "../../PreviousAnswer"
import {KlassDropdown} from "components/Select"

type Props = {
    field: any
    canSign: boolean
    errorFieldIds: string[]
    fieldRefs: MutableRefObject<Record<string, HTMLDivElement>>
    onChangeField: (fieldId: string, data: any) => void
}

export function Select(props: Props) {
    const {field, canSign, errorFieldIds, fieldRefs, onChangeField} = props
    const [zIndex, setZIndex] = useState(1)
    const {top, left, width, height} = field.placeInformation
    const {isYourField} = field
    const dropdownRef = useRef(null)

    const dropdownHeight = useMemo(() => {
        let result = Math.floor(height) - 2
        while (result >= width / 3) {
            result /= 2
        }
        return Math.floor(result)
    }, [height, width])

    const fontSize = useMemo(() => {
        let result = Math.min(Math.floor(dropdownHeight / 1.7), 14)
        if (isMobile) {
            const minFontsize = dropdownHeight <= 14 ? 7 : 9
            result = Math.min(Math.floor(dropdownHeight / 1.7), minFontsize)
        }
        return result
    }, [dropdownHeight])

    const dropdownStyles = useMemo(
        () => ({
            control: (provided, state) => ({
                ...provided,
                background: "#fff",
                border: "none",
                minHeight: dropdownHeight,
                height: dropdownHeight,
                position: "relative",
                boxShadow: state.isFocused ? null : null
            }),
            valueContainer: (provided) => ({
                ...provided,
                height: dropdownHeight,
                padding: "0 3px",
                fontSize,
                position: "static"
            }),
            singleValue: (provided) => ({
                ...provided,
                height: dropdownHeight,
                top: 0,
                display: "flex",
                alignItems: "center",
                transform: "none"
            }),
            placeholder: (provided) => ({
                ...provided,
                height: dropdownHeight,
                top: 0,
                display: "flex",
                alignItems: "center",
                transform: "none"
            }),
            input: (provided) => ({
                ...provided,
                margin: "0px"
            }),
            indicatorsContainer: (provided) => ({
                ...provided,
                height: dropdownHeight
            }),
            option: (provided) => ({
                ...provided,
                fontSize,
                padding: "4px 6px"
            })
        }),
        [dropdownHeight, fontSize]
    )

    const onFillInput = () => {
        if (!canSign || !isYourField) {
            return
        }
        onChangeField(field.id, {isSigned: true, value: field.defaultData})
        setTimeout(() => {
            if (dropdownRef.current) {
                dropdownRef.current.focus()
            }
        }, 0)
    }

    const onChangeValue = (newValue) => {
        onChangeField(field.id, {value: newValue})
    }

    const renderSelectContent = () => {
        if (field?.readOnly) {
            return <div className={styles.studentDataValue}>{field.value}</div>
        }
        return (
            <KlassDropdown
                dropdownRef={dropdownRef}
                options={field.fieldValues}
                labelKey="option"
                value={field.value}
                stylesCustom={dropdownStyles}
                openMenuOnFocus
                onChange={onChangeValue}
                onMenuOpen={() => setZIndex(2)}
                onMenuClose={() => setZIndex(1)}
                isClearable
            />
        )
    }

    const renderField = () => {
        if (!field.isSigned) {
            return (
                <div
                    ref={(ref) => (fieldRefs.current[field.id] = ref)}
                    className={cx(styles.actionItem, {
                        [styles.actionItemRequired]: isYourField && field.required,
                        [styles.hasComment]: !!field.remarkComment,
                        [styles.actionItem_error]: errorFieldIds.includes(field.id)
                    })}
                    style={{width, height}}
                    onClick={onFillInput}>
                    <div className={styles.textWrap}>
                        <p className={styles.label}>{field.label}</p>
                        <Icon icon="ARROW_DOWN" className={styles.icon} />
                    </div>
                </div>
            )
        }
        return (
            <div
                ref={(ref) => (fieldRefs.current[field.id] = ref)}
                className={cx(styles.actionItem, {
                    [styles.actionItemRequired]: field.isYourField && field.required,
                    [styles.hasComment]: !!field.remarkComment,
                    [styles.actionItem_error]: errorFieldIds.includes(field.id)
                })}
                style={{width, height}}>
                <div className={cx(styles.inputWrap, "taskSubmissionField")} style={{fontSize}}>
                    {renderSelectContent()}
                </div>
            </div>
        )
    }

    const renderBody = () => {
        if (!canSign || !isYourField) {
            return <PreviousAnswer field={field} backgroundColor={field.backgroundColor} />
        }
        return renderField()
    }

    return (
        <>
            <div className={styles.wrap} style={{left, top, zIndex}}>
                {renderBody()}
            </div>
            {field.remarkComment && (
                <div className={styles.wrap} style={{left, top, zIndex: 9}}>
                    <RemarkIndicator field={field} />
                </div>
            )}
        </>
    )
}
